'use client'
import * as React from 'react'
import {
  ArrowDown,
  ArrowUp,
  Check,
  Copy,
  ExternalLink,
  Grip,
  Heart,
  Instagram,
  Plus,
  RotateCcw,
  ShoppingBag,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ColorPicker } from '@/components/ui/color-picker'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { appSignupUrl } from '@/lib/widget/embed'

// Pure-browser shoppable link-in-bio composer. The visitor assembles a
// mobile bio page (avatar, bio, product/UGC links) and sees a real live
// preview inside a phone frame. Nothing is hosted here: the "Publish"
// CTA hands the composed page to the Idukki app, which hosts it and
// keeps prices + stock in sync with the connected store. Honest split:
// this tool composes, the app hosts + syncs.

type BioLink = {
  id: string
  label: string
  url: string
  price: string
  kind: 'product' | 'ugc' | 'link'
}

type Layout = 'list' | 'grid'

const ACCENTS = ['#CDEB45', '#F97362', '#7C8BFF', '#FF7AC6', '#34D399', '#FBBF24']

let idSeq = 0
const nextId = () => `lnk-${idSeq++}`

const SAMPLE: BioLink[] = [
  { id: nextId(), label: 'Airlift High-Waist Suit Up Shorts', url: 'shop.example.com/airlift', price: '64.76', kind: 'product' },
  { id: nextId(), label: 'White Sweater Green Stripes', url: 'shop.example.com/white-sweater', price: '110.76', kind: 'product' },
  { id: nextId(), label: 'As seen on @maya.k', url: 'instagram.com/p/abc', price: '', kind: 'ugc' },
  { id: nextId(), label: 'Shop the full edit', url: 'shop.example.com', price: '', kind: 'link' },
]

const KIND_META: Record<BioLink['kind'], { label: string; icon: React.ReactNode }> = {
  product: { label: 'Product', icon: <ShoppingBag className="h-3.5 w-3.5" aria-hidden /> },
  ugc: { label: 'UGC tile', icon: <Heart className="h-3.5 w-3.5" aria-hidden /> },
  link: { label: 'Link', icon: <ExternalLink className="h-3.5 w-3.5" aria-hidden /> },
}

function initials(name: string): string {
  const parts = name.replace(/[@#]/g, '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'ID'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[1][0]).toUpperCase()
}

export function LinkInBioBuilder() {
  const [handle, setHandle] = React.useState('@your.brand')
  const [bio, setBio] = React.useState('Real fits from real customers. Tap a tile to shop the look.')
  const [accent, setAccent] = React.useState('#CDEB45')
  const [layout, setLayout] = React.useState<Layout>('list')
  const [links, setLinks] = React.useState<BioLink[]>(SAMPLE)
  const [copied, setCopied] = React.useState(false)

  function addLink() {
    setLinks((prev) => [...prev, { id: nextId(), label: 'New link', url: '', price: '', kind: 'link' }])
  }
  function removeLink(id: string) {
    setLinks((prev) => prev.filter((l) => l.id !== id))
  }
  function patchLink(id: string, patch: Partial<BioLink>) {
    setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)))
  }
  function move(id: string, dir: -1 | 1) {
    setLinks((prev) => {
      const i = prev.findIndex((l) => l.id === id)
      const j = i + dir
      if (i < 0 || j < 0 || j >= prev.length) return prev
      const next = [...prev]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  }
  function reset() {
    setHandle('@your.brand')
    setBio('Real fits from real customers. Tap a tile to shop the look.')
    setAccent('#CDEB45')
    setLayout('list')
    setLinks(SAMPLE.map((l) => ({ ...l, id: nextId() })))
  }

  function copySummary() {
    const lines = [
      `Link-in-bio page: ${handle}`,
      bio,
      '',
      ...links.map((l) => `• [${KIND_META[l.kind].label}] ${l.label}${l.price ? ` — $${l.price}` : ''}${l.url ? ` (${l.url})` : ''}`),
    ]
    navigator.clipboard?.writeText(lines.join('\n')).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      },
      () => {},
    )
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:gap-12">
      {/* Editor */}
      <div className="rounded-2xl border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b p-5 sm:p-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Compose your page</p>
            <h2 className="mt-1 font-display text-xl font-medium tracking-tight sm:text-2xl">Build the bio link</h2>
          </div>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors duration-150 hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <RotateCcw className="h-3 w-3" aria-hidden /> Reset
          </button>
        </div>

        <div className="space-y-6 p-5 sm:p-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Handle or brand name" htmlFor="lib-handle">
              <Input id="lib-handle" value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="@your.brand" />
            </Field>
            <Field label="Accent colour" htmlFor="lib-accent">
              <div className="flex items-center gap-2">
                <div className="flex flex-wrap gap-1.5">
                  {ACCENTS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setAccent(c)}
                      aria-label={`Use accent ${c}`}
                      aria-pressed={accent === c}
                      className={cn(
                        'h-7 w-7 rounded-full border transition-transform duration-150 motion-safe:hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        accent === c ? 'ring-2 ring-foreground ring-offset-2 ring-offset-card' : 'border-border',
                      )}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
                <ColorPicker value={accent} onChange={setAccent} id="lib-accent" ariaLabel="Custom accent colour" />
              </div>
            </Field>
          </div>

          <Field label="Bio" htmlFor="lib-bio" hint="One line a shopper reads before tapping. Keep it human.">
            <textarea
              id="lib-bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={2}
              maxLength={140}
              className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span className="mt-1 block text-right text-[11px] text-muted-foreground">{bio.length}/140</span>
          </Field>

          <fieldset>
            <legend className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Layout</legend>
            <div className="mt-2 flex gap-2" role="radiogroup" aria-label="Layout">
              {(['list', 'grid'] as Layout[]).map((l) => (
                <button
                  key={l}
                  type="button"
                  role="radio"
                  aria-checked={layout === l}
                  onClick={() => setLayout(l)}
                  className={cn(
                    'rounded-full border px-3.5 py-1.5 text-xs font-medium capitalize transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    layout === l ? 'border-primary bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:text-foreground',
                  )}
                >
                  {l === 'list' ? 'List' : 'Grid of tiles'}
                </button>
              ))}
            </div>
          </fieldset>

          <div>
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Links &amp; tiles</p>
              <span className="text-[11px] text-muted-foreground">{links.length} item{links.length === 1 ? '' : 's'}</span>
            </div>

            <ul className="mt-3 space-y-3">
              {links.map((link, i) => (
                <li key={link.id} className="rounded-xl border bg-background p-3">
                  <div className="flex items-start gap-2">
                    <Grip className="mt-2 h-4 w-4 shrink-0 text-muted-foreground/60" aria-hidden />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(Object.keys(KIND_META) as BioLink['kind'][]).map((k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => patchLink(link.id, { kind: k })}
                            aria-pressed={link.kind === k}
                            className={cn(
                              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              link.kind === k ? 'border-primary bg-primary/10 text-foreground' : 'text-muted-foreground hover:text-foreground',
                            )}
                          >
                            {KIND_META[k].icon}
                            {KIND_META[k].label}
                          </button>
                        ))}
                      </div>
                      <label className="sr-only" htmlFor={`label-${link.id}`}>
                        Link label
                      </label>
                      <Input
                        id={`label-${link.id}`}
                        value={link.label}
                        onChange={(e) => patchLink(link.id, { label: e.target.value })}
                        placeholder="Label shoppers see"
                        className="h-9"
                      />
                      <div className="grid grid-cols-[1fr_auto] gap-2">
                        <div>
                          <label className="sr-only" htmlFor={`url-${link.id}`}>
                            Destination URL
                          </label>
                          <Input
                            id={`url-${link.id}`}
                            value={link.url}
                            onChange={(e) => patchLink(link.id, { url: e.target.value })}
                            placeholder="shop.example.com/product"
                            className="h-9"
                          />
                        </div>
                        <div className="w-24">
                          <label className="sr-only" htmlFor={`price-${link.id}`}>
                            Price (optional)
                          </label>
                          <div className="relative">
                            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                            <Input
                              id={`price-${link.id}`}
                              value={link.price}
                              onChange={(e) => patchLink(link.id, { price: e.target.value })}
                              placeholder="Price"
                              inputMode="decimal"
                              className="h-9 pl-6"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => move(link.id, -1)}
                        disabled={i === 0}
                        aria-label={`Move ${link.label} up`}
                        className="rounded-md border p-1 text-muted-foreground transition-colors duration-150 enabled:hover:text-foreground disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(link.id, 1)}
                        disabled={i === links.length - 1}
                        aria-label={`Move ${link.label} down`}
                        className="rounded-md border p-1 text-muted-foreground transition-colors duration-150 enabled:hover:text-foreground disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLink(link.id)}
                        aria-label={`Remove ${link.label}`}
                        className="rounded-md border p-1 text-muted-foreground transition-colors duration-150 hover:border-destructive/40 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={addLink}
              className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed py-2.5 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plus className="h-4 w-4" aria-hidden /> Add a link or tile
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <p className="text-xs text-muted-foreground sm:text-sm">
            Composed in your browser. Hosting and live product sync happen in the Idukki app.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm">
              <a href={appSignupUrl({ ref: 'tool-link-in-bio' })}>Publish in the app</a>
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={copySummary}>
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" aria-hidden /> Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" aria-hidden /> Copy summary
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Live phone preview */}
      <div className="lg:sticky lg:top-24 lg:self-start">
        <p className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 opacity-75 motion-safe:animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          Live preview
        </p>
        <PhonePreview handle={handle} bio={bio} accent={accent} layout={layout} links={links} />
      </div>
    </div>
  )
}

function PhonePreview({
  handle,
  bio,
  accent,
  layout,
  links,
}: {
  handle: string
  bio: string
  accent: string
  layout: Layout
  links: BioLink[]
}) {
  return (
    <div
      className="mx-auto w-full max-w-[320px] rounded-[40px] border-[10px] border-foreground/90 bg-foreground/90 p-1.5 shadow-[0_30px_70px_-30px_rgba(0,0,0,0.5)]"
      style={{ borderRadius: 40 }}
    >
      <div className="relative overflow-hidden rounded-[28px] bg-background">
        {/* Notch */}
        <div className="absolute left-1/2 top-2 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-foreground/90" aria-hidden />
        <div className="max-h-[560px] overflow-y-auto px-5 pb-8 pt-10">
          {/* Avatar + identity */}
          <div className="flex flex-col items-center text-center">
            <div
              className="flex h-16 w-16 items-center justify-center rounded-full text-lg font-bold"
              style={{ backgroundColor: accent, color: '#0b1120' }}
            >
              {initials(handle)}
            </div>
            <p className="mt-3 font-display text-base font-semibold text-foreground">{handle || '@your.brand'}</p>
            <p className="mt-1.5 max-w-[220px] text-[13px] leading-snug text-muted-foreground">{bio}</p>
            <span
              className="mt-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide"
              style={{ backgroundColor: `${accent}22`, color: '#0b1120' }}
            >
              <Instagram className="h-3 w-3" aria-hidden /> Shoppable bio
            </span>
          </div>

          {/* Links */}
          {links.length === 0 ? (
            <p className="mt-8 rounded-xl border border-dashed p-5 text-center text-xs text-muted-foreground">
              Add a link to see it appear here.
            </p>
          ) : layout === 'list' ? (
            <ul className="mt-6 space-y-2.5">
              {links.map((l) => (
                <li key={l.id}>
                  <div
                    className="flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-transform duration-150 motion-safe:hover:-translate-y-0.5"
                    style={{ borderColor: `${accent}55` }}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span className="shrink-0" style={{ color: accent === '#CDEB45' ? '#0b1120' : accent }}>
                        {KIND_META[l.kind].icon}
                      </span>
                      <span className="truncate text-[13px] font-medium text-foreground">{l.label || 'Untitled'}</span>
                    </span>
                    {l.price && (
                      <span
                        className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold"
                        style={{ backgroundColor: accent, color: '#0b1120' }}
                      >
                        ${l.price}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <ul className="mt-6 grid grid-cols-2 gap-2.5">
              {links.map((l) => (
                <li key={l.id}>
                  <div
                    className="flex h-full flex-col justify-between rounded-2xl border p-3 transition-transform duration-150 motion-safe:hover:-translate-y-0.5"
                    style={{ borderColor: `${accent}55` }}
                  >
                    <span style={{ color: accent === '#CDEB45' ? '#0b1120' : accent }}>{KIND_META[l.kind].icon}</span>
                    <span className="mt-3 line-clamp-2 text-[12px] font-medium leading-snug text-foreground">{l.label || 'Untitled'}</span>
                    {l.price && (
                      <span className="mt-2 inline-block w-fit rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: accent, color: '#0b1120' }}>
                        ${l.price}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="mt-7 text-center text-[10px] text-muted-foreground/70">Built with idukki</p>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="block text-sm">
      <label htmlFor={htmlFor} className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </label>
      {hint && <span className="mt-0.5 block text-[11px] text-muted-foreground">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </div>
  )
}
